import {
  find,
  get,
  getOr,
  includes,
  isEmpty,
  map,
  negate,
  pipe,
  size,
  toArray,
  toNumber,
} from 'lodash/fp'
import createCachedSelector from 're-reselect'
import { Selector, createSelector } from 'reselect'

import { passProps } from '@masterplandev/utils'

import { generateBasicSelectors } from '@/core/selectors'
import cachedKeyCreator from '@/core/utils/redux/cachedKeyCreator'

import { FILTER_USE_ALL } from '../constants'
import { LearnpathsState } from '../types/LearnpathsState'
import * as buildApiUrl from '../utils/buildApiUrl'

export const rootSelector: Selector<
  LearnpathsState,
  LearnpathsState['learnpaths']
> = get('learnpaths')

export const learnpathSelector = createCachedSelector(
  [rootSelector, passProps<{ learnpathId: string }>],
  (root, { learnpathId }) => root[buildApiUrl.managed(learnpathId)] ?? {},
)(cachedKeyCreator)

export const isLearnpathContentModifiedSelector = createSelector(
  learnpathSelector,
  getOr(false, 'modified'),
)

export const learnpathFiltersSelector = createSelector(
  learnpathSelector,
  getOr([], 'filters'),
)

export const isFetchAllUsersFilterOnSelector = createSelector(
  learnpathFiltersSelector,
  includes(FILTER_USE_ALL),
)

export const learnpathMembersSelectors = generateBasicSelectors(
  createCachedSelector(
    [rootSelector, passProps<{ learnpathId: string }>],
    (root, { learnpathId }) =>
      getOr({}, buildApiUrl.members(learnpathId), root),
  )((_, { learnpathId }) => learnpathId),
)

export const learnpathMembersListSelector = createSelector(
  learnpathMembersSelectors.data,
  getOr([], 'members'),
)

export const learnpathMembersIdsSelector = createSelector(
  learnpathMembersListSelector,
  map('id'),
)

export const candidatesSelectors = generateBasicSelectors(
  createCachedSelector(
    [rootSelector, passProps<{ learnpathId: string }>],
    (root, { learnpathId }) => get(buildApiUrl.candidates(learnpathId), root),
  )(cachedKeyCreator),
)

export const possibleCandidatesCountSelector = createSelector(
  candidatesSelectors.data,
  getOr(0, 'num_all_students'),
)

export const proposedCandidatesSelector = createSelector(
  candidatesSelectors.data,
  get('candidates'),
)

export const proposedCandidatesCountSelector = createSelector(
  proposedCandidatesSelector,
  size,
)

export const matchingCandidatesSelector = createSelector(
  candidatesSelectors.data,
  get('members'),
)

export const selectedCandidatesSelector = createSelector(
  candidatesSelectors.root,
  getOr([], 'selected'),
)

export const selectedCandidatesIdsSelector = createSelector(
  selectedCandidatesSelector,
  map('id'),
)

export const selectedCandidatesCountSelector = createSelector(
  selectedCandidatesSelector,
  size,
)

export const filteredCandidatesSelector = createSelector(
  proposedCandidatesSelector,
  selectedCandidatesSelector,
  matchingCandidatesSelector,
  (_: LearnpathsState, props: { learnpathId: string; isSearching?: boolean }) =>
    Boolean(props?.isSearching),
  (proposed, selected, matching, isSearching) => {
    if (!isSearching) {
      return selected
    }

    return [...toArray(proposed), ...toArray(matching)]
  },
)

export const platformTopicsRootSelectors = generateBasicSelectors(
  createSelector(
    [rootSelector, passProps<{ learnpathId: string }>],
    (root, { learnpathId }) =>
      getOr({}, buildApiUrl.platformTopics(learnpathId), root),
  ),
)

export const platformTopicsSelector = createSelector(
  platformTopicsRootSelectors.data,
  getOr([], 'topics'),
)

export const platformTopicsCountSelector = createSelector(
  platformTopicsRootSelectors.data,
  getOr(0, 'total_count'),
)

export const platformTopicsFiltersSelector = createSelector(
  platformTopicsRootSelectors.data,
  getOr({}, 'filters'),
)

export const platformTopicsIdsSelector = createSelector(
  platformTopicsSelector,
  map('id'),
)

export const platformTopicSelector = createSelector(
  [platformTopicsSelector, passProps],
  (topics, { topicId }) => find(['id', toNumber(topicId)], topics),
)

export const selectedPlatformTopicsSelector = createSelector(
  platformTopicsRootSelectors.root,
  getOr([], 'selected'),
)

export const platformTopicsLoadingSelector = createSelector(
  platformTopicsRootSelectors.root,
  get('fetching'),
)

export const selectedPlatformTopicLecturesIdsSelector = createSelector(
  [selectedPlatformTopicsSelector, passProps],
  (topics, { topicId }) =>
    pipe([find(['id', topicId]), get('selectedLectures'), map('id')])(topics),
)

export const selectedPlatformTopicLecturesCountSelector = createSelector(
  [selectedPlatformTopicsSelector, passProps],
  (topics, { topicId }) =>
    pipe([find(['id', topicId]), get('selectedLectures'), size])(topics),
)

export const platformTopicCatalogLectureSelector = createSelector(
  [platformTopicSelector, passProps],
  (topic, { lectureId }) =>
    pipe([get('lectures'), find(['id', toNumber(lectureId)])])(topic),
)

export const learnpathUserGroupsSelectors = generateBasicSelectors(
  createCachedSelector(
    [rootSelector, passProps<{ learnpathId: string }>],
    (root, { learnpathId }) =>
      getOr({}, buildApiUrl.userGroups(learnpathId), root),
  )((_, { learnpathId }) => learnpathId),
)

export const learnpathUserGroupsListSelector = createSelector(
  learnpathUserGroupsSelectors.data,
  getOr([], 'user_groups'),
)

export const learnpathUserGroupItemSelector = createSelector(
  [learnpathUserGroupsListSelector, passProps],
  (groups, { userGroupId }) => find(['id', userGroupId], groups),
)

export const learnpathUserGroupIsAssignedSelector = createSelector(
  learnpathUserGroupItemSelector,
  negate(isEmpty),
)

export const learnpathUserGroupsCounterSelector = createSelector(
  learnpathUserGroupsListSelector,
  size,
)

export const learnpathUserGroupsSelected = createSelector(
  rootSelector,
  getOr({} as Record<string, string[]>, 'userGroups'),
)

export const learnpathUserGroupsSelectedIdsSelector = createSelector(
  [learnpathUserGroupsSelected, passProps<{ learnpathId: string }>],
  (learnpaths, { learnpathId }) => learnpaths[learnpathId],
)

export const learnpathUserGroupsSelectedCountSelector = createSelector(
  learnpathUserGroupsSelectedIdsSelector,
  size,
)

export const learnpathUserGroupsSelectedHasId = createSelector(
  [learnpathUserGroupsSelectedIdsSelector, passProps<{ userGroupId: string }>],
  (userGroupIds, { userGroupId }) => includes(userGroupId, userGroupIds),
)
